import { type DrawItem, DrawItemListSchema } from "@/types/DrawItem";
import type { ProjectInfo, ProjectSettings } from "@/types/Projects";
import type { Retainage } from "@/types/Retainage";
import { v4 as uuidv4 } from "uuid";
import type { GridApi } from "ag-grid-community";
import { BudgetColumns } from "@/enums/BudgetColumns";
import { testBit } from "./Utility";

export const createNewDrawItem = (drawId: string | undefined): DrawItem => {
  return {
    drawItemId: uuidv4(),
    parentDrawItemId: "",
    drawId: drawId ?? "",
    ordinal: 1,
    lineRefNumber: "",
    description: "",
    costType: "INSP",
    custCostType: "HARD",
    budgetAmount: 0,
    budgetAdjustment: 0,
    adjustedBudget: 0,
    totalPriorDraw: 0,
    totalThisDraw: 0,
    netThisDraw: 0,
    priorDrawnToDate: 0,
    drawnToDate: 0,
    variance: 0,
    percentDrawn: 0,
    percentBudget: 0,
    percentInspectedThisDraw: 0,
    percentInspected: 0,
    percentInspectedPriorDraw: 0,
    totalRetainagePriorDraw: 0,
    totalReleasedPriorDraw: 0,
    totalRetainage: 0,
    totalReleased: 0,
    retainageBalance: 0,
    fundsRequired: 0,
    fundsAvailable: 0,
    projectedOver: 0,
    lienWaiverReceived: false,
    finalLienWaiver: false,
    cutChecks: true,
    entityId: "",
    totalPriorEquity: 0,
    totalThisEquity: 0,
    priorEquityToDate: 0,
    equityToDate: 0,
    totalDrawnToDate: 0,
    netThisDrawEquity: 0,
    calcPercentDraw: false,
    predecessor: null,
    flags: 0,
    rootItemId: "",
    hccPercent: 0,
    initialBudget: 0,
    cumulativeAdjustment: 0,
    newItem: true,
  };
};

export const createNewRetainage = (item: DrawItem, projectEntityId: string | undefined): Retainage => {
  return {
    lineRefNumber: item.lineRefNumber,
    description: item.description,
    retainageId: 0,
    drawItemId: item.drawItemId,
    projectEntityId: projectEntityId ?? "",
    thisDrawLoan: item.totalThisDraw,
    thisDrawEquity: item.totalThisEquity,
    retainagePriorDraw: 0,
    retainageThisDraw: 0,
    totalRetainagePriorDraw: 0,
    totalRetainage: 0,
    releasedPriorDraw: 0,
    releasedThisDraw: 0,
    totalReleasedPriorDraw: 0,
    totalReleased: 0,
    retainageBalance: 0,
  };
};

export const recalculateRows = async (items: DrawItem[], projectSettings: ProjectSettings): Promise<DrawItem[]> => {
  let recalcItems: DrawItem[] = [];
  await axios
    .post("/draw/recalculate-rows", { items: items, projectSettings: projectSettings })
    .then((response) => {
      recalcItems = DrawItemListSchema.parse(response.data.result);
    })
    .catch((e) => errorApi(e));

  //If the recalcItems is empty that means something went wrong, so just return the original values
  if (recalcItems.length === 0) return items;
  return recalcItems;
};

export const createFooterRow = (category: string): Object => {
  return {
    description: category,
    initialBudget: 0,
    budgetAmount: 0,
    budgetAdjustment: 0,
    cumulativeAdjustment: 0,
    adjustedBudget: 0,
    priorDrawnToDate: 0,
    totalThisDraw: 0,
    totalThisEquity: 0,
    netThisDraw: 0,
    priorEquityToDate: 0,
    projectedOver: 0,
    drawnToDate: 0,
    netThisDrawEquity: 0,
    equityToDate: 0,
    totalDrawnToDate: 0,
    variance: 0,
    fundsAvailable: 0,
    fundsRequired: 0,
    totalRetainagePriorDraw: 0,
    totalReleasedPriorDraw: 0,
    totalRetainage: 0,
    totalReleased: 0,
    retainageBalance: 0,
  };
};

const addToTotal = (category: string, item: DrawItem, amount: number): number => {
  return category === "TOTAL" || category === item.custCostType ? amount : 0;
};

export const updateTotals = (footers: any, drawItems: Ref<DrawItem[]>, gridApi: Ref<GridApi | undefined>) => {
  const categories: string[] = ["TOTAL", ...new Set(drawItems.value.map((item: DrawItem) => item.custCostType))];
  //REMOVE NON-EXISTING CATEGORIES
  footers.value = footers.value.filter((footer: any) => footer.description in categories);
  //ADD NEW CATEGORIES
  categories.forEach((category: string) => {
    if (footers.value.find((footer: any) => footer.description === category) == null) footers.value.push(createFooterRow(category));
  });

  footers.value.forEach((footer: any) => {
    const category = footer.description;
    footer.initialBudget = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.initialBudget), 0);
    footer.budgetAmount = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.budgetAmount), 0);
    footer.budgetAdjustment = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.budgetAdjustment), 0);
    footer.cumulativeAdjustment = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.cumulativeAdjustment), 0);
    footer.adjustedBudget = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.adjustedBudget), 0);
    footer.priorDrawnToDate = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.priorDrawnToDate), 0);
    footer.totalThisDraw = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalThisDraw), 0);
    footer.totalThisEquity = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalThisEquity), 0);
    footer.netThisDraw = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.netThisDraw), 0);
    footer.priorEquityToDate = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.priorEquityToDate), 0);
    footer.projectedOver = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.projectedOver), 0);
    footer.drawnToDate = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.drawnToDate), 0);
    footer.netThisDrawEquity = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.netThisDrawEquity), 0);
    footer.equityToDate = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.equityToDate), 0);
    footer.totalDrawnToDate = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalDrawnToDate), 0);
    footer.variance = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.variance), 0);
    footer.fundsAvailable = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.fundsAvailable), 0);
    footer.fundsRequired = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.fundsRequired), 0);
    footer.totalRetainagePriorDraw = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalRetainagePriorDraw), 0);
    footer.totalReleasedPriorDraw = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalReleasedPriorDraw), 0);
    footer.totalRetainage = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalRetainage), 0);
    footer.totalReleased = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalReleased), 0);
    footer.retainageBalance = drawItems.value.reduce((acc, curr) => acc + addToTotal(category, curr, curr.retainageBalance), 0);
  });

  gridApi.value?.setGridOption("pinnedBottomRowData", footers.value);
};

export const showColumns = (projectInfo: ProjectInfo, columns: number | null, gridApi: Ref<GridApi | undefined>) => {
  const visible: string[] = [];
  const invisible: string[] = [];
  Object.entries(BudgetColumns).forEach((entry) => {
    if (entry[1].alwaysVisible) return;
    if (entry[0] == "cutChecks") {
      if (projectInfo.checkingAccount == null || (columns != null && !testBit(columns, entry[1].bit))) invisible.push(entry[0]);
      else visible.push(entry[0]);
    } else {
      if (columns == null || testBit(columns, entry[1].bit)) visible.push(entry[0]);
      else invisible.push(entry[0]);
    }
  });
  gridApi.value?.setColumnsVisible(visible, true);
  gridApi.value?.setColumnsVisible(invisible, false);
  gridApi.value?.sizeColumnsToFit();
};
